import React, { useEffect, useMemo, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useNavigate } from 'react-router-dom';
import { Box, CssBaseline, CircularProgress } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import Login from './components/Login';
import Home from './components/home/Home';
import CashInList from './components/cash_in/CashInList';
import CategoriesList from './components/categories/CategoriesList';
import CashInForm from './components/cash_in/CashInForm';
import OperationForm from './components/operations/OperationsForm';
import OperationsList from './components/operations/OperationsList';
import DashboardLayout from './components/layout/DashboardLayout';
import createAppTheme from './theme';
import CashInDashboard from './components/cash_in/CashInDashboard';
import UserProfile from './components/user/Profile';
import Register from './components/user/Register';
import { SpeedInsights } from '@vercel/speed-insights/react';
import CashOutList from './components/cash_out/CashOutList';
import AnnualBudget from './components/budget/AnnualBudget';
import CashOutForm from './components/cash_out/CashOutForm';
import CashOutDashboard from './components/cash_out/CashOutDashboard';
import BalanceDashboard from './components/balance/BalanceDashboard';
import { setNavigator } from './navigation';
import walletApi from './api/wallet';
import PositionsPage from './components/positions/PositionsPage';
import { clearAuthData } from './utils/auth';
import { GoogleAuthWrapper } from './components/auth/GoogleAuthProvider';
import { CurrencyProvider } from './contexts/CurrencyContext';

const validateToken = async (setIsAuthenticated, setLoading, navigate) => {
  try {
    const token = await walletApi.refresh();
    if (token) {
      setIsAuthenticated(true);
    } else {
      clearAuthData();
      navigate('/login');
    }
  } catch (error) {
    clearAuthData();
    navigate('/login');
  } finally {
    setLoading(false);
  }
};

const PrivateRoute = ({ children, colorMode, onToggleColorMode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    setNavigator(navigate);
    validateToken(setIsAuthenticated, setLoading, navigate);
  }, [navigate]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return isAuthenticated ? (
    <DashboardLayout colorMode={colorMode} onToggleColorMode={onToggleColorMode}>
      {children}
    </DashboardLayout>
  ) : (
    <Navigate to="/login" />
  );
};

function App() {
  const [colorMode, setColorMode] = useState(() => {
    return localStorage.getItem('colorMode') || 'light';
  });

  const theme = useMemo(() => createAppTheme(colorMode), [colorMode]);

  const handleToggleColorMode = () => {
    setColorMode((prev) => {
      const next = prev === 'light' ? 'dark' : 'light';
      localStorage.setItem('colorMode', next);
      return next;
    });
  };

  return (
    <GoogleAuthWrapper>
      <CurrencyProvider>
        <Router>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <SpeedInsights />
            <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={<Navigate to="/home" />} />
            <Route
              path="/home"
              element={
                <PrivateRoute colorMode={colorMode} onToggleColorMode={handleToggleColorMode}>
                  <Home />
                </PrivateRoute>
              }
            />
            <Route path="/user/register" element={<Register />} />
            <Route
              path="/operations/register"
              element={
                <PrivateRoute colorMode={colorMode} onToggleColorMode={handleToggleColorMode}>
                  <OperationForm />
                </PrivateRoute>
              }
            />
            <Route
              path="/operations/list"
              element={
                <PrivateRoute colorMode={colorMode} onToggleColorMode={handleToggleColorMode}>
                  <OperationsList />
                </PrivateRoute>
              }
            />
            <Route
              path="/cashin/list"
              element={
                <PrivateRoute colorMode={colorMode} onToggleColorMode={handleToggleColorMode}>
                  <CashInList />
                </PrivateRoute>
              }
            />
            <Route
              path="/cashin/register"
              element={
                <PrivateRoute colorMode={colorMode} onToggleColorMode={handleToggleColorMode}>
                  <CashInForm />
                </PrivateRoute>
              }
            />
            <Route
              path="/cashin/dashboard"
              element={
                <PrivateRoute colorMode={colorMode} onToggleColorMode={handleToggleColorMode}>
                  <CashInDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/cashout/list"
              element={
                <PrivateRoute colorMode={colorMode} onToggleColorMode={handleToggleColorMode}>
                  <CashOutList />
                </PrivateRoute>
              }
            />
            <Route
              path="/cashout/register"
              element={
                <PrivateRoute colorMode={colorMode} onToggleColorMode={handleToggleColorMode}>
                  <CashOutForm />
                </PrivateRoute>
              }
            />
            <Route
              path="/cashout/dashboard"
              element={
                <PrivateRoute colorMode={colorMode} onToggleColorMode={handleToggleColorMode}>
                  <CashOutDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/planejamento"
              element={
                <PrivateRoute colorMode={colorMode} onToggleColorMode={handleToggleColorMode}>
                  <AnnualBudget />
                </PrivateRoute>
              }
            />
            <Route
              path="/categories/list"
              element={
                <PrivateRoute colorMode={colorMode} onToggleColorMode={handleToggleColorMode}>
                  <CategoriesList />
                </PrivateRoute>
              }
            />
            <Route
              path="/balance"
              element={
                <PrivateRoute colorMode={colorMode} onToggleColorMode={handleToggleColorMode}>
                  <BalanceDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/user/profile"
              element={
                <PrivateRoute colorMode={colorMode} onToggleColorMode={handleToggleColorMode}>
                  <UserProfile />
                </PrivateRoute>
              }
            />
            <Route
              path="/positions"
              element={
                <PrivateRoute colorMode={colorMode} onToggleColorMode={handleToggleColorMode}>
                  <PositionsPage />
                </PrivateRoute>
              }
            />
            </Routes>
          </ThemeProvider>
        </Router>
      </CurrencyProvider>
    </GoogleAuthWrapper>
  );
}

export default App;